import { useEffect, useState } from "react";
import { Loader2, UserRound } from "lucide-react";

import type { Resident, ResidentPayload } from "@/api/residents";
import type { TrustComplaintQueue } from "@/api/trust_complaints";
import { ResidentActivity } from "@/components/trust_suite/ResidentActivity";
import { ResidentAddresses } from "@/components/trust_suite/ResidentAddresses";
import { ResidentConcernsList } from "@/components/trust_suite/ResidentConcernsList";
import { ResidentPermitsList } from "@/components/trust_suite/ResidentPermitsList";
import { ResidentTasks } from "@/components/trust_suite/ResidentTasks";
import { DetailNotesCard } from "@/components/workflow_tracking/DetailNotesCard";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type ResidentDetailProps = {
  municipalityId: number;
  resident: Resident | null;
  loading?: boolean;
  updating?: boolean;
  onUpdate: (patch: ResidentPayload) => void;
  onOpenComplaint?: (complaintId: number, queue: TrustComplaintQueue) => void;
  onRefresh?: () => void;
};

export function ResidentDetail({
  municipalityId,
  resident,
  loading = false,
  updating = false,
  onUpdate,
  onOpenComplaint,
  onRefresh,
}: ResidentDetailProps) {
  const [notesDraft, setNotesDraft] = useState(resident?.internal_notes ?? "");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    setNotesDraft(resident?.internal_notes ?? "");
  }, [resident?.id, resident?.internal_notes]);

  useEffect(() => {
    setActiveTab("overview");
  }, [resident?.id]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading resident…
      </div>
    );
  }

  if (!resident) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
        <UserRound className="h-10 w-10 opacity-30" />
        <p className="text-sm">Select a resident to view details</p>
      </div>
    );
  }

  const notesDirty = notesDraft.trim() !== (resident.internal_notes ?? "").trim();
  const customFields =
    resident.metadata?.custom_fields && typeof resident.metadata.custom_fields === "object"
      ? (resident.metadata.custom_fields as Record<string, unknown>)
      : {};

  const saveNotes = () => {
    const next = notesDraft.trim();
    if (next !== (resident.internal_notes ?? "").trim()) {
      onUpdate({ internal_notes: next || null });
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="resident-detail">
      <div className="shrink-0 border-b bg-background px-4 py-4 sm:px-6">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="font-normal">
              Resident
            </Badge>
            {resident.open_complaint_count > 0 ? (
              <Badge variant="outline" className="font-normal">
                {resident.open_complaint_count} open concern
                {resident.open_complaint_count === 1 ? "" : "s"}
              </Badge>
            ) : null}
          </div>
          <h2 className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">
            {resident.display_name}
          </h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>#{resident.id}</span>
            {resident.last_interaction_at ? (
              <span>Last activity {formatRelativeTime(resident.last_interaction_at)}</span>
            ) : null}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b px-4 py-2 sm:px-6">
          <TabsList className="h-9 w-full justify-start sm:w-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="concerns">Concerns</TabsTrigger>
            <TabsTrigger value="permits">Permits</TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="min-w-0">
          <TabsContent value="overview" className="m-0 space-y-4 px-4 py-4 sm:px-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4 rounded-lg border p-4">
                <h3 className="text-sm font-medium">Contact</h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="resident-display-name">Display name</Label>
                    <Input
                      id="resident-display-name"
                      defaultValue={resident.display_name}
                      disabled={updating}
                      onBlur={(event) => {
                        const value = event.target.value.trim();
                        if (value && value !== resident.display_name) {
                          onUpdate({ display_name: value });
                        }
                      }}
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="resident-email">Email</Label>
                      <Input
                        id="resident-email"
                        defaultValue={resident.email ?? ""}
                        disabled={updating}
                        onBlur={(event) => {
                          const value = event.target.value.trim();
                          if (value !== (resident.email ?? "")) {
                            onUpdate({ email: value || null });
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="resident-phone">Phone</Label>
                      <Input
                        id="resident-phone"
                        defaultValue={resident.phone ?? ""}
                        disabled={updating}
                        onBlur={(event) => {
                          const value = event.target.value.trim();
                          if (value !== (resident.phone ?? "")) {
                            onUpdate({ phone: value || null });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <DetailNotesCard
                  id="resident-internal-notes"
                  value={notesDraft}
                  onChange={setNotesDraft}
                  onSave={saveNotes}
                  dirty={notesDirty}
                  saving={updating}
                  placeholder="Context for future interactions, preferences, handoffs…"
                />
                {Object.keys(customFields).length > 0 ? (
                  <div className="rounded-lg border p-4">
                    <h3 className="text-sm font-medium">Custom fields</h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      {Object.entries(customFields).map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-4">
                          <dt className="text-muted-foreground">{key}</dt>
                          <dd className="font-medium">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="mb-3 text-sm font-medium">Related addresses</h3>
              <ResidentAddresses
                municipalityId={municipalityId}
                resident={resident}
                onUpdated={() => onRefresh?.()}
              />
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="m-0 px-4 py-4 sm:px-6">
            <ResidentActivity municipalityId={municipalityId} residentId={resident.id} />
          </TabsContent>

          <TabsContent value="tasks" className="m-0 px-4 py-4 sm:px-6">
            <ResidentTasks
              municipalityId={municipalityId}
              residentId={resident.id}
              onInvalidate={() => onRefresh?.()}
            />
          </TabsContent>

          <TabsContent value="concerns" className="m-0 px-4 py-4 sm:px-6">
            <ResidentConcernsList
              municipalityId={municipalityId}
              residentId={resident.id}
              onOpenComplaint={onOpenComplaint}
            />
          </TabsContent>

          <TabsContent value="permits" className="m-0 px-4 py-4 sm:px-6">
            <ResidentPermitsList municipalityId={municipalityId} residentId={resident.id} />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
